import { useState, useMemo, useEffect, useRef } from 'react'
import { dateFormat, numberFormat } from 'shared/util'

import { KojinResult } from 'interface/kojin'

import {
  Box,
  Alert,
  Stack,
  StackProps,
  Pagination,
  Button,
  Typography,
  Chip,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableSortLabel,
  TableCellProps,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material'

import { DownloadIcon } from 'shared/icons'

import { visuallyHidden } from '@mui/utils'

import { SisetuDoctorsConditions, isValid } from './condition'

type CellValue = {
  code?: string | null
  text: string | number | null
  style?: string
  unit?: string
}

type ColumnKey =
  'rank' |
  'cn' |
  'photo' |
  'name' |
  'kana' |
  'section' |
  'post' |
  'birth' |
  'age' |
  'sex' |
  'native' |
  'school' |
  'curse' |
  'gradYear' |
  'associate' |
  'registerNo' |
  'kaigyou' |
  'department' |
  'conference' |
  'speciality' |
  'otherHospital'

type Order = 'asc' | 'desc'

type ColumnDef = {
  key: ColumnKey
  title: string
  align: 'left' | 'center' | 'right' | 'chips' | 'image'
  hidden?: boolean
  val: (record: KojinResult) => CellValue | CellValue[] | null | undefined
  compare: (a: KojinResult, b: KojinResult) => number
}

const ListCountInPage = 200

const DoctorPhotoCell = (
  props: {
    vals: string[]
    columnKey: string
    record: KojinResult
  } & TableCellProps
) => {
  const { vals, record, columnKey, ...others } = props
  return (
    <TableCell {...others} padding="none" align="center">
      {vals.map((v, i) => (
        <img
          key={`${record.kojinCode}-${columnKey}-${i}`}
          src={v}
          alt={record.isiName ?? '医師'}
          style={{
            maxWidth: '2rem',
            maxHeight: '2rem',
            verticalAlign: 'top',
            border: '1px solid #fff',
            borderRadius: '4px',
            boxShadow: '0px 0px 2px rgba(0,0,0,0.5)',
            margin: '2px',
          }}
        />
      ))}
    </TableCell>
  )
}

const DoctorChipCell = (props: {
  vals: string[]
  columnKey: string
  record: KojinResult
}) => {
  const { vals, columnKey, record } = props
  return (
    <TableCell style={{ width: 0, whiteSpace: 'nowrap' }}>
      <Stack direction="row" spacing={0.5}>
        {vals.map((text, i) => (
          <Chip
            key={`${record.kojinCode}_${columnKey}_${i}`}
            label={text}
            size="small"
          />
        ))}
      </Stack>
    </TableCell>
  )
}

const SisetuPreviewDoctorsTableCell = (props: {
  column: ColumnDef
  record: KojinResult
}) => {
  const { column, record } = props
  const val = column.val(record)
  if (val !== null && val !== undefined) {
    if (column.align === 'image') {
      const vals = (Array.isArray(val) ? val : [val]).flatMap(
        v => v.text && typeof v.text === 'string' ? v.text : []
      )
      return <DoctorPhotoCell vals={vals} record={record} columnKey={column.key} />
    }
    if (column.align === 'chips') {
      const vals = (Array.isArray(val) ? val : [val]).flatMap(
        v => (
          v.text && typeof v.text === 'number' ? numberFormat(v.text) :
          v.text && typeof v.text === 'string' ? v.text : []
        )
      )
      return <DoctorChipCell vals={vals} record={record} columnKey={column.key} />
    }
    const text = (Array.isArray(val) ? val : [val]).flatMap(
      v => (
        v.text && typeof v.text === 'number' ? numberFormat(v.text) : 
        v.text && typeof v.text === 'string' ? v.text : []
      )
    ).join(', ')
    if (text !== '') {
      const unit = Array.isArray(val) ? val.length > 0 ? val[0].unit : null : val.unit
      return (
        <TableCell
          align={column.align}
          style={{ width: 0, whiteSpace: 'nowrap' }}
        >
          {text}
          {unit && (
            <Typography
              variant="body2"
              component="span"
              fontSize="80%"
              color="text.secondary"
            >
              {' '}
              {unit}
            </Typography>
          )}
        </TableCell>
      )
    }
  }
  return <TableCell aria-disabled style={{ background: 'GhostWhite' }} />
}

type SisetuPreviewDoctorsTableProps = {
  sisetuCode: string
  kojinList?: KojinResult[]
  condition: SisetuDoctorsConditions
  onKojinOpen: (kojinCode: string) => void
} & StackProps
export const SisetuPreviewDoctorsTable = ({
  sisetuCode,
  kojinList,
  condition,
  onKojinOpen,
  ...others
}: SisetuPreviewDoctorsTableProps) => {
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down('sm'))

  const [pageNo, setPageNo] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<ColumnKey>('name')
  const [order, setOrder] = useState<Order>('asc')
  const tableRef = useRef<HTMLTableElement | null>(null)

  const columns = useMemo<ColumnDef[]>(
    () => [{
      key: 'rank',
      title: '順位',
      align: 'right',
      val: (record) => ({ text: record.score?.rank ? `${numberFormat(record.score.rank)} 位` : '' } as CellValue),
      compare: (a, b) => {
        return (a.score?.rank ?? Infinity) - (b.score?.rank ?? Infinity)
      },
    }, {
      key: 'cn',
      title: '会員',
      align: 'center',
      val: (record) => ({ text: record.mrpStats?.rank.name ?? '' } as CellValue),
      compare: (a, b) => {
        if (!a.mrpStats) return b.mrpStats ? 1 : 0
        if (!b.mrpStats) return -1
        return (a.mrpStats?.rank.code ?? '').localeCompare(b.mrpStats?.rank.code ?? '')
      },
    }, {
      key: 'photo',
      title: '顔',
      align: 'image',
      val: (record) => ({ text: record.actor?.photoLink ?? null } as CellValue),
      compare: () => 0,
    }, {
      key: 'name',
      title: '名前',
      align: 'left',
      val: (record) => ({ text: record.isiName } as CellValue),
      compare: (a, b) => {
        return (a.isiNameKana ?? a.isiName ?? '').localeCompare(b.isiNameKana ?? b.isiName ?? '')
      },
    }, {
      key: 'kana',
      title: 'かな',
      align: 'left',
      val: (record) => ({ text: record.isiNameKana } as CellValue),
      compare: (a, b) => {
        return (a.isiNameKana ?? a.isiName ?? '').localeCompare(b.isiNameKana ?? b.isiName ?? '')
      },
    }, {
      key: 'section',
      title: '部課',
      align: 'left',
      val: (record) => {
        return record.organ?.filter(organ => organ.sisetuCode === sisetuCode)
          .map(organ => ({ code: organ.bukaCode, text: organ.bukaName } as CellValue))
      },
      compare: (a, b) => {
        const bukaA = a.organ?.filter(organ => organ.sisetuCode === sisetuCode)
          .map(organ => organ.bukaCode)
          .join('-') ?? ''
        const bukaB = b.organ?.filter(organ => organ.sisetuCode === sisetuCode)
          .map((organ) => organ.bukaCode)
          .join('-') ?? ''
        return bukaA.localeCompare(bukaB)
      },
    }, {
      key: 'post',
      title: '役職',
      align: 'left',
      val: (record) => {
        return record.organ?.filter(organ => organ.sisetuCode === sisetuCode)
          .map(organ => ({ code: organ.yakusyokuCode, text: organ.yakusyokuName } as CellValue))
      },
      compare: (a, b) => {
        const yakusyokuA = a.organ?.filter(organ => organ.sisetuCode === sisetuCode)
          .map(organ => (organ.positionRank ?? 10000) * 1000 + parseInt(organ.yakusyokuCode ?? '0'))
          .reduce((p, c) => p + c, 0) ?? 0
        const yakusyokuB = b.organ?.filter(organ => organ.sisetuCode === sisetuCode)
          .map(organ => (organ.positionRank ?? 10000) * 1000 + parseInt(organ.yakusyokuCode ?? '0'))
          .reduce((p, c) => p + c, 0) ?? 0
        return yakusyokuA < yakusyokuB ? -1 : yakusyokuB < yakusyokuA ? 1 : 0
      },
    }, {
      key: 'birth',
      title: '生年月日',
      align: 'center',
      val: (record) => record.seinenGappi ? ({ text: dateFormat(record.seinenGappi) } as CellValue) : null,
      compare: (a, b) => (a.seinenGappi ?? '').localeCompare(b.seinenGappi ?? '')
    }, {
      key: 'age',
      title: '年齢',
      align: 'right',
      val: (record) => record.nenrei ? ({ text: record.nenrei, unit: '歳' } as CellValue) : null,
      compare: (a, b) => {
        const ageA = a.nenrei ?? 0
        const ageB = b.nenrei ?? 0
        return ageA < ageB ? -1 : ageB < ageA ? 1 : 0
      },
    }, {
      key: 'sex',
      title: '性別',
      align: 'center',
      val: (record) => (
        record.seibetuCode === '1' ? ({ text: '男性', style: 'male' } as CellValue) :
        record.seibetuCode === '2' ? ({ text: '女性', style: 'female' } as CellValue) : null
      ),
      compare: (a, b) => (a.seibetuCode ?? '').localeCompare(b.seibetuCode ?? '')
    }, {
      hidden: true,
      key: 'native',
      title: '出身地',
      align: 'left',
      val: (record) => record.syussinchiName ? ({ text: record.syussinchiName } as CellValue) : null,
      compare: (a, b) => (a.syussinchiCode ?? '').localeCompare(b.syussinchiCode ?? '')
    }, {
      key: 'school',
      title: '卒業校',
      align: 'left',
      val: (record) => record.sotukouName ? ({ text: record.sotukouName } as CellValue) : null,
      compare: (a, b) => (a.sotukouCode ?? '').localeCompare(b.sotukouCode ?? '')
    }, {
      hidden: true,
      key: 'curse',
      title: '学部',
      align: 'left',
      val: (record) => record.gakubuName ? ({ text: record.gakubuName } as CellValue) : null,
      compare: (a, b) => (a.gakubuCode ?? '').localeCompare(b.gakubuCode ?? '')
    }, {
      key: 'gradYear',
      title: '卒年',
      align: 'left',
      val: (record) => record.sotugyouYear ? ({ text: `${record.sotugyouYear}`, unit: '年' } as CellValue) : null,
      compare: (a, b) => {
        const yearA = a.sotugyouYear ?? 0
        const yearB = b.sotugyouYear ?? 0
        return yearA < yearB ? -1 : yearB < yearA ? 1 : 0
      }
    }, {
      key: 'associate',
      title: '医師会',
      align: 'left',
      val: (record) => record.isikaiName ? ({ text: record.isikaiName } as CellValue) : null,
      compare: (a, b) => (a.isikaiCode ?? '').localeCompare(b.isikaiCode ?? '')
    }, {
      hidden: true,
      key: 'registerNo',
      title: '医師登録番号',
      align: 'left',
      val: (record) => record.isiTourokuNo ? ({ text: record.isiTourokuNo } as CellValue) : null,
      compare: (a, b) => (a.isiTourokuNo ?? '').localeCompare(b.isiTourokuNo ?? '')
    }, {
      key: 'kaigyou',
      title: '開業',
      align: 'center',
      val: (record) => {
        if (record.kaikinCode === '1') {
          if (record.kaigyouYear) {
            return { text: `${record.kaigyouYear}`, unit: '年' } as CellValue
          } else {
            return { text: '◯', style: 'kaigyoui' } as CellValue
          }
        }
        return null
      },
      compare: (a, b) => {
        const kaigyouA = a.kaikinCode === '1' ? (a.kaigyouYear ? a.kaigyouYear : 1) : 0
        const kaigyouB = b.kaikinCode === '1' ? (b.kaigyouYear ? b.kaigyouYear : 1) : 0
        return kaigyouA < kaigyouB ? -1 : kaigyouB < kaigyouA ? 1 : 0
      }
    }, {
      key: 'department',
      title: '診療科',
      align: 'chips',
      val: (record) => record.sinryoka?.map(sinryoka => ({ code: sinryoka.sinryokaCode, text: sinryoka.sinryokaName } as CellValue)),
      compare: (a, b) => {
        const sinryokaA = a.sinryoka?.map(sinryoka => sinryoka.sinryokaCode).join('-') ?? ''
        const sinryokaB = b.sinryoka?.map(sinryoka => sinryoka.sinryokaCode).join('-') ?? ''
        return sinryokaA.localeCompare(sinryokaB)
      }
    }, {
      key: 'conference',
      title: '学会',
      align: 'chips',
      val: (record) => record.gakkai?.map(gakkai => ({ code: gakkai.gakkaiCode, text: gakkai.gakkaiName } as CellValue)),
      compare: (a, b) => {
        const gakkaiA = a.gakkai?.map((gakkai) => gakkai.gakkaiCode).join('-') ?? ''
        const gakkaiB = b.gakkai?.map((gakkai) => gakkai.gakkaiCode).join('-') ?? ''
        return gakkaiA.localeCompare(gakkaiB)
      }
    }, {
      key: 'speciality',
      title: '専門医',
      align: 'chips',
      val: (record) => record.senmoni?.map(senmoni => ({ code: senmoni.senmoniCode, text: senmoni.senmoniName } as CellValue)),
      compare: (a, b) => {
        const senmoniA = a.senmoni?.map((senmoni) => senmoni.senmoniCode).join('-') ?? ''
        const senmoniB = b.senmoni?.map((senmoni) => senmoni.senmoniCode).join('-') ?? ''
        return senmoniA.localeCompare(senmoniB)
      },
    }, {
      key: 'otherHospital',
      title: '他施設',
      align: 'chips',
      val: (record) => record.organ?.filter(organ => organ.sisetuCode !== sisetuCode).map(organ => ({ code: organ.sisetuCode, text: organ.sisetuName } as CellValue)),
        compare: (a, b) => {
          const otherA = a.organ?.filter(organ => organ.sisetuCode !== sisetuCode)
            .map(sisetu => sisetu.sisetuCode)
            .join('-') ?? ''
          const otherB = b.organ?.filter((organ) => organ.sisetuCode !== sisetuCode)
            .map(sisetu => sisetu.sisetuCode)
            .join('-') ?? ''
          return otherA.localeCompare(otherB)
        },
      },
    ],
    [sisetuCode]
  )

  const onChangeOrder = (key: ColumnKey) => {
    if (orderBy === key) {
      setOrder(order === 'asc' ? 'desc' : 'asc')
    } else {
      setOrderBy(key)
      setOrder('asc')
    }
  }
  const onDownload = () => {
    // todo!!
  }

  const sortedList = useMemo<KojinResult[] | null>(
    () => {
      if (!kojinList) return null

      const sortedList = kojinList.filter(kojin => isValid(sisetuCode, condition, kojin))
      sortedList.sort((a, b) => {
        const column = columns.find((column) => column.key === orderBy)
        return (order === 'asc' ? 1 : -1) * (column?.compare(a, b) ?? 0)
      })
      return sortedList
    },
    [kojinList, condition, orderBy, order, columns, sisetuCode]
  )

  useEffect(
    () => {
      setPageNo(1)
      tableRef.current?.scroll(0, 0)
    },
    [sortedList]
  )

  const pageOffset = (pageNo - 1) * ListCountInPage
  const pageLast = pageOffset + ListCountInPage
  const pageCount = sortedList
    ? Math.floor((sortedList.length - 1) / ListCountInPage) + 1
    : 0

  return (
    <Stack direction="column" {...others}>
      <Box sx={{ flexGrow: 1, overflow: 'auto', position: 'relative' }}>
        {sortedList && sortedList.length === 0 ? (
          <Box padding={1}>
            <Alert severity="info">対象が見つかりません.</Alert>
          </Box>
        ) : (
          <TableContainer sx={{ width: '100%', height: '100%' }}>
            <Table
              aria-label="doctors table"
              stickyHeader
              size="small"
              ref={tableRef}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    key="index"
                    style={{ width: 0, whiteSpace: 'nowrap' }}
                  >
                    #
                  </TableCell>
                  {columns
                    .filter((column) => !column.hidden)
                    .map((column) => {
                      return column.align === 'image' ? (
                        <TableCell
                          key={column.key}
                          style={{
                            width: 0,
                            whiteSpace: 'nowrap',
                            paddingLeft: 0,
                            paddingRight: 0,
                          }}
                          align="center"
                        >
                          {column.title}
                        </TableCell>
                      ) : (
                        <TableCell
                          key={column.key}
                          style={{ width: 0, whiteSpace: 'nowrap' }}
                          sortDirection={orderBy === column.key ? order : false}
                        >
                          <TableSortLabel
                            active={orderBy === column.key}
                            direction={orderBy === column.key ? order : 'asc'}
                            onClick={() => onChangeOrder(column.key)}
                          >
                            {column.title}
                            {orderBy === column.key ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc'
                                  ? 'sorted descending'
                                  : 'sorted ascending'}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                      )
                    })}
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedList
                  ?.slice(pageOffset, pageLast)
                  ?.map((record, index) => {
                    return (
                      <TableRow
                        hover
                        key={`doctors-table-row-${record.kojinCode}-${index}`}
                        onClick={() => {
                          if (record.kojinCode) onKojinOpen(record.kojinCode)
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell
                          key={`${record.kojinCode}_index`}
                          align="right"
                          style={{ width: 0, whiteSpace: 'nowrap' }}
                        >
                          {pageOffset + index + 1}
                        </TableCell>
                        {columns
                          .filter((column) => !column.hidden)
                          .map((column) => (
                            <SisetuPreviewDoctorsTableCell
                              column={column}
                              record={record}
                              key={`${record.kojinCode}_${column.key}`}
                            />
                          ))}
                      </TableRow>
                    )
                  }) || <></>}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Stack
        padding={1}
        direction="row"
        justifyContent='center'
        alignItems='center'
        sx={{
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="button" paddingX={1} whiteSpace="nowrap">
          {numberFormat(sortedList?.length, '-')} 人
        </Typography>

        {sortedList ? (
          <Pagination
            shape="rounded"
            count={pageCount}
            siblingCount={isSmallMedia ? 0 : 5}
            defaultPage={1}
            onChange={(e, v) => {
              setPageNo(v)
              tableRef.current?.scroll(0, 0)
            }}
            sx={{ flexGrow: 1 }}
          />
        ) : (
          <Box sx={{ flexGrow: 1 }} />
        )}
        {isSmallMedia ? (
          <IconButton
            size="small"
            disabled={!sortedList || sortedList?.length === 0}
            onClick={onDownload}
          >
            <DownloadIcon />
          </IconButton>
        ) : (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={!sortedList || sortedList?.length === 0}
            onClick={onDownload}
            sx={{
              whiteSpace: 'nowrap'
            }}
          >
            ダウンロード
          </Button>
        )}
      </Stack>
    </Stack>
  )
}
